/**
 * useWaRealtimeSub — wrapper estándar para listeners de Supabase Realtime.
 *
 * Resuelve 3 problemas del patrón actual (22+ archivos con listeners ad-hoc):
 *   1. Cleanup garantizado: el canal se remueve en unmount SIN excepción.
 *   2. Tenant safety: si el payload no incluye el tenant esperado, se ignora.
 *      Evita que datos de otro tenant lleguen al handler ante una RLS débil.
 *   3. Estandarización: un solo patrón para todos los módulos. Más fácil
 *      migrar a dedup por canal (un canal por tenant×tabla) en Fase 4.
 *
 * Uso básico:
 *   useWaRealtimeSub({
 *     channelKey: `wa-conv-details-${conversationId}`,
 *     table: 'wa_conversations',
 *     filter: `id=eq.${conversationId}`,
 *     event: 'UPDATE',
 *     tenantId,
 *     onData: (payload) => { ... },
 *   });
 *
 * Multi-tabla (mismo canal):
 *   useWaRealtimeSub({
 *     channelKey: `wa-contact-${tenantId}-${phone}`,
 *     subscriptions: [
 *       { table: 'wa_conversations', filter: `contact_phone=eq.${phone}`, event: 'INSERT', onData: ... },
 *       { table: 'wa_conversations', filter: `contact_phone=eq.${phone}`, event: 'UPDATE', onData: ... },
 *     ],
 *     tenantId,
 *   });
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { RealtimePostgresChangesPayload, RealtimePostgresChangesFilter } from '@supabase/supabase-js';

type RealtimeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

type DataHandler = (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;

interface SingleSub {
  table: string;
  filter?: string;
  event?: RealtimeEvent;
  onData: DataHandler;
}

interface Options {
  /** Clave única del canal. Mismo key = mismo canal de Supabase (dedup). */
  channelKey: string;
  /** tenant_id esperado. Si el payload.new o payload.old tiene otro tenant_id, se ignora. */
  tenantId: string | null | undefined;
  /** Una sola suscripción (shorthand). */
  table?: string;
  filter?: string;
  event?: RealtimeEvent;
  onData?: DataHandler;
  /** Múltiples suscripciones sobre el mismo canal. */
  subscriptions?: SingleSub[];
  /** Si false, no crea el canal (útil para deshabilitar condicionalmente). */
  enabled?: boolean;
}

export function useWaRealtimeSub(opts: Options): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const { channelKey, tenantId, table, filter, event, onData, subscriptions, enabled = true } = optsRef.current;

    if (!enabled || !tenantId) return;

    const subs: SingleSub[] = subscriptions ?? (table && onData
      ? [{ table, filter, event: event ?? '*', onData }]
      : []);

    if (subs.length === 0) return;

    /*
     * 🚨 CADA MONTAJE TIENE SU PROPIO CANAL, Y NO ES UN DETALLE.
     *
     * El founder lo reportó así: renombró una línea de «Shalom» a «Nutrizen», el formulario mostró
     * el nombre nuevo y la lista de Canales de la izquierda siguió diciendo «Shalom» hasta recargar
     * la página.
     *
     * La causa estaba acá. Antes, varios componentes que pedían el mismo `channelKey` compartían
     * topic, y este bloque removía «el canal viejo» antes de crear el suyo:
     *
     *   1. la barra lateral se suscribe            → canal vivo
     *   2. se abre la pantalla de la línea, que se suscribe con la MISMA key
     *      → le saca el canal a la barra y crea el suyo
     *   3. esa pantalla cambia de sección y se desmonta
     *      → su cleanup remueve el canal, y la barra queda suscrita a NADA
     *
     * Desde ahí la barra no se entera de ningún cambio hasta que se recarga. No era «el realtime
     * anda mal»: era un componente apagándole el realtime a otro sin que nadie lo notara.
     *
     * 🪤 Y EL CLEANUP TENÍA SU MOTIVO: `supabase.channel(K)` con la misma K devuelve el MISMO
     * objeto, y llamar `.on()` sobre uno ya suscrito revienta con «cannot add postgres_changes
     * callbacks after subscribe()». Con un sufijo único por montaje esa colisión no puede ocurrir,
     * así que el cleanup deja de hacer falta — y de romper.
     *
     * 🪤 `channelKey` SIGUE SIENDO EL DE QUIEN LLAMA: sirve para leer los canales en depuración y
     * para el día que se haga dedup de verdad (con conteo de suscriptores, no destruyendo).
     */
    const topicUnico = `${channelKey}::${Math.random().toString(36).slice(2, 10)}`;
    let ch = supabase.channel(topicUnico);

    for (const sub of subs) {
      ch = ch.on(
        'postgres_changes',
        // El `event` es dinámico (union); Supabase tipa un overload por literal de
        // evento, así que declaramos el filtro como el de "todos los eventos" ('*').
        {
          event: sub.event ?? '*',
          schema: 'public',
          table: sub.table,
          ...(sub.filter ? { filter: sub.filter } : {}),
        } as RealtimePostgresChangesFilter<'*'>,
        (payload) => {
          // Tenant safety: ignorar si el payload no pertenece al tenant esperado.
          // Cubre el caso donde RLS permite ver filas de otro tenant (bug de seguridad
          // separado, pero no queremos que llegue al state de la UI).
          const row = (payload.new ?? payload.old ?? {}) as Record<string, unknown>;
          if (row.tenant_id && row.tenant_id !== tenantId) return;

          sub.onData(payload as RealtimePostgresChangesPayload<Record<string, unknown>>);
        },
      );
    }

    ch.subscribe();

    return () => {
      void supabase.removeChannel(ch);
    };
    // Solo re-crea el canal cuando cambie la key o el tenant.
    // Los handlers viven en optsRef para no reciclar el canal por cada render.
     
  }, [opts.channelKey, opts.tenantId, opts.enabled]);
}
